/*
PWA entry point.
Boot order: open IndexedDB, load species index, decide cold-start route
based on onboardingDone, mount router, register service worker.

The SW is only registered in production builds — Vite's dev server would
otherwise serve the SW with a wrong MIME / break HMR.
*/

import { startRouter, kPaths } from './router'
import { openDb } from './data/db'
import { ensureSpeciesIndex } from './data/speciesIndex'
import { isOnboardingDone } from './data/onboardingStore'
import { installSwUpdateBanner } from './ui/components/SwUpdateBanner'
import { installInstallPrompt } from './ui/components/InstallPrompt'
import './app.css'

const kSwUrl: string = '/sw.js'  // service worker served from public root

async function boot(): Promise<void>
{
	const vRoot = document.getElementById('app') as HTMLElement | null  // mount node from index.html
	if (!vRoot)
	{
		console.error('Missing #app root in index.html')
		return
	}

	let vInitialHash: string | null = null  // forced startDestination, null when the URL hash drives the route
	try
	{
		await openDb()
		await ensureSpeciesIndex()
		const vDone = await isOnboardingDone()
		if (!vDone && !window.location.hash) vInitialHash = kPaths.welcome()
	}
	catch (vErr)
	{
		console.error('Boot failed during data init', vErr)
	}

	startRouter(vRoot, vInitialHash)
	installSwUpdateBanner()  // listen for SW activations triggered by registerServiceWorker below
	installInstallPrompt()   // capture beforeinstallprompt + surface the iOS Add-to-Home-Screen hint
	registerServiceWorker()
}

/*
Register the service worker. iOS Safari requires HTTPS (or localhost) and
the file to be served from the registered scope.
*/
function registerServiceWorker(): void
{
	if (!('serviceWorker' in navigator)) return
	if (!import.meta.env.PROD) return
	navigator.serviceWorker
		.register(kSwUrl, { scope: '/' })
		.catch((inErr: unknown) => { console.warn('SW registration failed', inErr) })
}

void boot()
